"use client"

import type React from "react"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { Calendar, MapPin, Mail, Plus, X, AlertTriangle } from "lucide-react"
import DatePicker from "react-datepicker"
import { format } from "date-fns"
import type { ScheduleFormData, ValidationErrors, UserType } from "../components/types/application.types"
import { validateDate, validateStartTime, validateEndTime } from "../components/utils/validation.utils"
import { isValidEmail } from "../components/utils/application.utils"
import { toast } from "react-hot-toast"

interface ScheduleFormProps {
  scheduleFormData: ScheduleFormData
  setScheduleFormData: React.Dispatch<React.SetStateAction<ScheduleFormData>>
  validationErrors: ValidationErrors
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>
  emailInput: string
  setEmailInput: React.Dispatch<React.SetStateAction<string>>
  currentRoundForAction: any
  user: UserType | null
  isScheduling: boolean
  onSubmit: () => void
  onCancel: () => void
  selectedApplication?: { job: { id: string } } | null
  onFetchJobLocation?: (jobId: string) => void
  isRescheduleMode?: boolean
}

export function ScheduleForm({
  scheduleFormData,
  setScheduleFormData,
  validationErrors,
  setValidationErrors,
  emailInput,
  setEmailInput,
  currentRoundForAction,
  user,
  isScheduling,
  onSubmit,
  onCancel,
  selectedApplication,
  onFetchJobLocation,
  isRescheduleMode,
}: ScheduleFormProps) {
  // Functions to handle additional interviewer emails
  const handleAddEmail = () => {
    if (emailInput.trim() && isValidEmail(emailInput.trim())) {
      if (!scheduleFormData.additionalParticipants.includes(emailInput.trim())) {
        setScheduleFormData((prev) => ({
          ...prev,
          additionalParticipants: [...prev.additionalParticipants, emailInput.trim()],
        }))
        setEmailInput("")
        toast.success(`Added: ${emailInput.trim()}`, {
          duration: 1500,
          style: { whiteSpace: 'nowrap' }
        })
      } else {
        toast.error("Already added", {
          duration: 1500,
          style: { whiteSpace: 'nowrap' }
        })
      }
    } else {
      toast.error("Invalid email", {
        duration: 1500,
        style: { whiteSpace: 'nowrap' }
      })
    }
  }

  const handleRemoveEmail = (emailToRemove: string) => {
    setScheduleFormData((prev) => ({
      ...prev,
      additionalParticipants: prev.additionalParticipants.filter((email) => email !== emailToRemove),
    }))
    toast.success("Removed", {
      duration: 1500,
      style: { whiteSpace: 'nowrap' }
    })
  }

  const handleEmailKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddEmail()
    }
  }

  return (
    <div className="w-full p-6 sm:p-8 rounded-xl mt-1">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="mb-3">
            <h3 className="text-xl font-bold text-gray-900">Schedule Interview</h3>
          </div>
        </div>
      </div>

      {/* Form Fields */}
      <div className="space-y-2">
        {/* Interview Type Toggle */}
        <div className="mb-10 mt-5" style={{ width: "70%" }}>
          <Label className="text-sm font-semibold text-gray-800 mb-3 block">
            Interview Type <span className="text-red-500">*</span>
          </Label>
          <div className="flex items-center gap-5">
            <div className="relative inline-flex bg-gray-100 rounded-xl p-1 shadow-sm">
              <button
                type="button"
                onClick={async () => {
                  setScheduleFormData((prev) => ({ ...prev, interviewType: "Offline" }))
                  // Fetch job location if switching to offline and no location is set
                  if (scheduleFormData.interviewType === "Online" && 
                      !scheduleFormData.location &&
                      selectedApplication?.job?.id && 
                      onFetchJobLocation) {
                    await onFetchJobLocation(selectedApplication.job.id)
                  }
                }}
                className={`relative px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  scheduleFormData.interviewType === "Offline"
                    ? "bg-orange-600 text-white shadow-md"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <div className="flex items-center gap-2">Offline</div>
              </button>
              <button
                type="button"
                onClick={() => setScheduleFormData((prev) => ({ ...prev, interviewType: "Online" }))}
                className={`relative px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  scheduleFormData.interviewType === "Online"
                    ? "bg-orange-600 text-white shadow-md"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <div className="flex items-center gap-2">Online</div>
              </button>
            </div>
          </div>
        </div>

        {/* Date & Timezone Row */}
        <div className="grid" style={{ width: "70%" }}>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-800">
              Interview Date <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <DatePicker
                selected={scheduleFormData.scheduledDate ? new Date(scheduleFormData.scheduledDate) : null}
                onChange={(date) => {
                  const formattedDate = date ? format(date, "yyyy-MM-dd") : ""
                  setScheduleFormData((prev) => ({ ...prev, scheduledDate: formattedDate }))

                  // Real-time validation for date
                  setTimeout(() => {
                    validateDate(formattedDate)
                  }, 100)
                }}
                minDate={new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)} // 10 days ago
                dateFormat="MMMM d, yyyy"
                placeholderText="Select interview date"
                className="w-full p-3 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                wrapperClassName="w-full"
                calendarClassName="shadow-lg border border-gray-200 rounded-lg"
              />
              <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" style={{ color: 'black' }} />
            </div>
          </div>

          {/* <div className="space-y-2 max-w-[400px]">
            <Label className="text-sm font-semibold text-gray-800">Time Zone</Label>
            <Select
              value={scheduleFormData.timeZone}
              onValueChange={(value) => setScheduleFormData((prev) => ({ ...prev, timeZone: value }))}
            >
              <SelectTrigger className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 shadow-sm">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Asia/Kolkata">India Standard Time (UTC+5:30)</SelectItem>
                <SelectItem value="UTC">UTC</SelectItem>
                <SelectItem value="America/New_York">EST (Eastern Time)</SelectItem>
                <SelectItem value="Europe/London">GMT (London)</SelectItem>
                <SelectItem value="America/Los_Angeles">PST (Pacific Time)</SelectItem>
              </SelectContent>
            </Select>
          </div> */}
        </div>

        {/* Start & End Time Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-6" style={{ width: "70%" }}>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-800">
              Start Time <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <input
                type="time"
                value={scheduleFormData.startTime}
                onChange={(e) => {
                  const newStartTime = e.target.value;
                  setScheduleFormData((prev) => ({ ...prev, startTime: newStartTime }));
                  // Real-time validation for start time
                  setTimeout(() => {
                    // Check if start time is less than end time
                    if (scheduleFormData.endTime && newStartTime >= scheduleFormData.endTime) {
                      setValidationErrors((prev) => ({ ...prev, startTime: true }));
                      toast.error("Start < End", { duration: 1500, style: { whiteSpace: 'nowrap' } });
                    } else {
                      setValidationErrors((prev) => ({ ...prev, startTime: false }));
                    }
                  }, 100);
                }}
                className={`max-w-[400px] w-full p-3 border rounded-lg text-sm text-black-700 focus:outline-none focus:ring-2 shadow-sm ${
                  validationErrors.startTime
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50"
                    : "border-gray-200 focus:ring-orange-500 focus:border-orange-500"
                }`}
                step="900"
                required
              />
            </div>
            {validationErrors.startTime && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                <span className="text-xs text-red-600">Please select a valid start time</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-800">
              End Time <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <input
                type="time"
                value={scheduleFormData.endTime}
                onChange={(e) => {
                  const newEndTime = e.target.value;
                  setScheduleFormData((prev) => ({ ...prev, endTime: newEndTime }));
                  // Real-time validation for end time
                  setTimeout(() => {
                    // Check if start time is less than end time
                    if (scheduleFormData.startTime && scheduleFormData.startTime >= newEndTime) {
                      setValidationErrors((prev) => ({ ...prev, endTime: true }));
                      toast.error("End > Start", { duration: 1500, style: { whiteSpace: 'nowrap' } });
                    } else {
                      setValidationErrors((prev) => ({ ...prev, endTime: false }));
                    }
                  }, 100);
                }}
                className={`max-w-[400px] w-full p-3 border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 shadow-sm ${
                  validationErrors.endTime
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50"
                    : "border-gray-200 focus:ring-orange-500 focus:border-orange-500"
                }`}
                step="900"
                required
              />
            </div>
            {validationErrors.endTime && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                <span className="text-xs text-red-600">End time must be after start time</span>
              </div>
            )}
          </div>
        </div>

        {/* Meeting Subject and Additional Interviewers Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-6" style={{ width: "70%" }}>
          {/* Meeting Subject */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-800">Interviewer</Label>
            <Input
              value={`${currentRoundForAction?.roundName || "Interview"} - ${user?.username || user?.email || "Admin"}`}
              readOnly
              className="max-w-[400px] w-full p-3 border border-gray-200 rounded-lg text-sm text-gray-700 bg-gray-50 shadow-sm"
            />
          </div>

          {/* Additional Interviewers */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-800">Additional Interviewers</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyPress={handleEmailKeyPress}
                placeholder="Enter email address and press Enter"
                className="max-w-[400px] flex-1 p-3 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              />
              <Button
                type="button"
                onClick={handleAddEmail}
                className=" bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-lg shadow-sm"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Display added emails - Below the input field */}
            {scheduleFormData.additionalParticipants.length > 0 && (
              <div className="space-y-2 mt-3">
                <Label className="text-xs text-gray-600">Added Email Addresses:</Label>
                <div className="flex flex-wrap gap-2">
                  {scheduleFormData.additionalParticipants.map((email, index) => (
                    <div
                      key={index}
                      className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1 shadow-sm"
                    >
                      <Mail className="h-3 w-3 text-blue-600" />
                      <span className="text-sm text-gray-800">{email}</span>
                      <Button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-red-100 text-gray-500 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Location Field - Only show for offline interviews */}
        {scheduleFormData.interviewType === "Offline" && (
          <div className="space-y-2" style={{ width: "70%" }}>
            <Label className="text-sm font-semibold text-gray-800">
              Interview Location <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              value={scheduleFormData.location || ""}
              onChange={(e) => setScheduleFormData((prev) => ({ ...prev, location: e.target.value }))}
              placeholder="Enter the physical location for the interview"
              className="max-w-[570px] w-full p-3 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              required
            />
            <div className="flex items-start gap-2 mt-1">
              <MapPin className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-gray-500">
                Please provide the complete address including building name, floor, and room number if applicable.
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div
          className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t border-gray-200"
          style={{ width: "70%" }}
        >
          <Button
            onClick={onCancel}
            variant="outline"
            className="px-6 py-3 text-sm font-semibold border-gray-300 hover:bg-gray-50 shadow-sm bg-transparent"
            style={{ borderRadius: "14px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isScheduling}
            className="px-6 py-3 text-sm font-semibold bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
            style={{ borderRadius: "14px" }}
          >
            {isScheduling
              ? (isRescheduleMode ? "Rescheduling..." : "Scheduling...")
              : (isRescheduleMode ? "Reschedule Interview" : "Schedule Interview")}
          </Button>
        </div>
      </div>
    </div>
  )
}
